import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Layer {
  layerId: string;
  layerType: 'background' | 'image' | 'design' | 'text' | 'region';
  layerName: string;
  visible?: boolean;
}

export interface ObjectProps {
  scale: number;
  angle: number;
  opacity: number;
  borderRadius?: number;
  blendMode?: string;
  left?: number;
  top?: number;
  scaledWidth?: number;
  scaledHeight?: number;
  originX?: string;
  originY?: string;
  text?: string;
  fontFamily?: string;
  fill?: string;
  textAlign?: string;
  fontWeight?: string | number;
  fontStyle?: string;
  fontSize?: number;
  charSpacing?: number;
  lineHeight?: number;
  src?: string;
  layerName?: string;
}

export interface RegionData {
  id: string;
  name: string;
  originalColor: string;
  maskUrl: string;
  bounds?: { x: number, y: number, width: number, height: number };
}

export interface CurrentDesign {
  design_id: string;
  title?: string;
  status?: string;
  seed?: string;
  version?: number;
  preview_url?: string;
  export_url?: { jpg?: string; png?: string; svg?: string } | string;
  is_claimed?: boolean;
  owner_name?: string;
  created_at?: string;
  updated_at?: string;
  canvas?: { width?: number; height?: number } | string;
  layers?: any[];
  regions?: any;
}

interface EditorState {
  layers: Layer[];
  /** Currently selected Fabric object_id / layerId. Separate from activeRegionId. */
  activeLayerId: string | null;
  selectedObjectProps: ObjectProps | null;
  regions: RegionData[];
  regionColors: Record<string, string>;
  activeRegionId: string | null;
  currentAssetId: string | null;
  currentDesign: CurrentDesign | null;
  isGenerating: boolean;
  isRestoringDesign: boolean;
  isSavingDesign: boolean;
}

const initialState: EditorState = {
  layers: [],
  activeLayerId: null,
  selectedObjectProps: null,
  regions: [],
  regionColors: {},
  activeRegionId: null,
  currentAssetId: null,
  currentDesign: null,
  isGenerating: false,
  isRestoringDesign: false,
  isSavingDesign: false,
};

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    setLayers: (state, action: PayloadAction<Layer[]>) => {
      state.layers = action.payload;
    },
    addLayer: (state, action: PayloadAction<Layer>) => {
      if (state.layers.some((layer) => layer.layerId === action.payload.layerId)) return;
      state.layers.push({ visible: true, ...action.payload });
    },
    removeLayer: (state, action: PayloadAction<string>) => {
      state.layers = state.layers.filter((layer) => layer.layerId !== action.payload);
      if (state.activeLayerId === action.payload) {
        state.activeLayerId = null;
      }
    },
    setLayerVisible: (state, action: PayloadAction<{ layerId: string; visible: boolean }>) => {
      const layer = state.layers.find((item) => item.layerId === action.payload.layerId);
      if (layer) layer.visible = action.payload.visible;
    },
    updateLayer: (state, action: PayloadAction<{ layerId: string; layerName?: string; visible?: boolean }>) => {
      const layer = state.layers.find((item) => item.layerId === action.payload.layerId);
      if (layer) {
        if (action.payload.layerName !== undefined) layer.layerName = action.payload.layerName;
        if (action.payload.visible !== undefined) layer.visible = action.payload.visible;
      }
    },
    reorderLayers: (state, action: PayloadAction<string[]>) => {
      const byId = new Map(state.layers.map((layer) => [layer.layerId, layer]));
      const next: Layer[] = [];
      action.payload.forEach((id) => {
        const layer = byId.get(id);
        if (layer) next.push(layer);
      });
      state.layers.forEach((layer) => {
        if (!action.payload.includes(layer.layerId)) next.push(layer);
      });
      state.layers = next;
    },
    setActiveLayerId: (state, action: PayloadAction<string | null>) => {
      state.activeLayerId = action.payload;
    },
    setSelectedObjectProps: (state, action: PayloadAction<ObjectProps | null>) => {
      state.selectedObjectProps = action.payload;
    },
    setDesignRegions: (state, action: PayloadAction<RegionData[]>) => {
      state.regions = action.payload;
      action.payload.forEach(r => {
        const loadedColor = (r as any).currentColor || (r as any).color || r.originalColor;
        if (loadedColor) {
          state.regionColors[r.id] = loadedColor;
        }
      });
      if (!state.activeRegionId && action.payload.length > 0) {
        state.activeRegionId = action.payload[0].id;
      }
    },
    setRegionColor: (state, action: PayloadAction<{ id: string; color: string }>) => {
      state.regionColors[action.payload.id] = action.payload.color;
    },
    setActiveRegionId: (state, action: PayloadAction<string | null>) => {
      state.activeRegionId = action.payload;
    },
    resetRegionColors: (state) => {
      state.regionColors = {};
      state.regions.forEach(r => {
        state.regionColors[r.id] = r.originalColor;
      });
    },
    removeRegion: (state, action: PayloadAction<string>) => {
      state.regions = state.regions.filter((r) => r.id !== action.payload);
      delete state.regionColors[action.payload];
      if (state.activeRegionId === action.payload) {
        state.activeRegionId = state.regions[0]?.id || null;
      }
    },
    clearRegions: (state) => {
      state.regions = [];
      state.regionColors = {};
      state.activeRegionId = null;
      state.currentAssetId = null;
    },
    setCurrentAssetId: (state, action: PayloadAction<string | null>) => {
      state.currentAssetId = action.payload;
    },
    setCurrentDesign: (state, action: PayloadAction<CurrentDesign | null>) => {
      state.currentDesign = action.payload;
    },
    syncGeneratedLayers: (state, action: PayloadAction<Layer[]>) => {
      const overlays = state.layers.filter((layer) => layer.layerType === 'image' || layer.layerType === 'text');
      state.layers = [
        ...action.payload.map((layer) => ({ visible: true, ...layer })),
        ...overlays,
      ];
    },
    setIsGenerating: (state, action: PayloadAction<boolean>) => {
      state.isGenerating = action.payload;
    },
    setIsRestoringDesign: (state, action: PayloadAction<boolean>) => {
      state.isRestoringDesign = action.payload;
    },
    setIsSavingDesign: (state, action: PayloadAction<boolean>) => {
      state.isSavingDesign = action.payload;
    },
  },
});

export const { setLayers, addLayer, removeLayer, setLayerVisible, updateLayer, reorderLayers, setActiveLayerId, setSelectedObjectProps, setDesignRegions, setRegionColor, setActiveRegionId, resetRegionColors, removeRegion, clearRegions, setCurrentAssetId, setCurrentDesign, syncGeneratedLayers, setIsGenerating, setIsRestoringDesign, setIsSavingDesign } = editorSlice.actions;
export const setSelectedLayerId = setActiveLayerId;
export default editorSlice.reducer;
